/**
 * SwipeDetector - Vanilla TypeScript swipe detection
 * Replacement for jquery.touchSwipe
 */

export interface SwipeOptions {
  swipeLeft?: () => void;
  swipeRight?: () => void;
  swipeUp?: () => void;
  swipeDown?: () => void;
  threshold?: number; // Minimum distance for swipe (default: 50px)
  allowedTime?: number; // Maximum time for swipe (default: 300ms)
}

export function attachSwipeDetector(element: HTMLElement, options: SwipeOptions): () => void {
  const threshold = options.threshold || 50;
  const allowedTime = options.allowedTime || 300;

  let startX = 0;
  let startY = 0;
  let startTime = 0;

  const handleTouchStart = (e: TouchEvent) => {
    const touch = e.touches[0];
    startX = touch.pageX;
    startY = touch.pageY;
    startTime = Date.now();
  };

  const handleTouchMove = (e: TouchEvent) => {
    e.preventDefault();
  };

  const handleTouchEnd = (e: TouchEvent) => {
    const touch = e.changedTouches[0];
    const distX = touch.pageX - startX;
    const distY = touch.pageY - startY;
    const elapsedTime = Date.now() - startTime;

    if (elapsedTime > allowedTime) return;

    // Determine if horizontal or vertical swipe
    if (Math.abs(distX) >= threshold && Math.abs(distY) <= threshold / 2) {
      // Horizontal swipe - prevent browser back/forward navigation
      e.preventDefault();

      if (distX < 0 && options.swipeLeft) {
        options.swipeLeft();
      } else if (distX > 0 && options.swipeRight) {
        options.swipeRight();
      }
    } else if (Math.abs(distY) >= threshold && Math.abs(distX) <= threshold / 2) {
      // Vertical swipe
      if (distY < 0 && options.swipeUp) {
        options.swipeUp();
      } else if (distY > 0 && options.swipeDown) {
        options.swipeDown();
      }
    }
  };

  element.addEventListener('touchstart', handleTouchStart, { passive: true });
  element.addEventListener('touchmove', handleTouchMove, { passive: false });
  element.addEventListener('touchend', handleTouchEnd, { passive: false });

  // Return cleanup function
  return () => {
    element.removeEventListener('touchstart', handleTouchStart);
    element.removeEventListener('touchmove', handleTouchMove);
    element.removeEventListener('touchend', handleTouchEnd);
  };
}
